import dayjs from './dayjs'
import {
  differenceInDays,
  endOfYear,
  isSameDay,
  isSameMonth,
  isThisMonth,
  isThisYear,
  startOfYear,
  subDays,
  subYears,
  subMonths,
} from 'date-fns'

/**
 * Generates an array of dates between a start and end date (inclusive).
 *
 * Creates a dayjs object for each day in the range, including both start and end dates.
 *
 * @param startDate - The start date (Date object or date string)
 * @param endDate - The end date (Date object or date string)
 * @returns Array of dayjs objects representing each date in the range
 *
 * @example
 * ```typescript
 * const start = new Date('2024-01-01')
 * const end = new Date('2024-01-03')
 * getDateRange(start, end) // returns [dayjs('2024-01-01'), dayjs('2024-01-02'), dayjs('2024-01-03')]
 * ```
 */
export const getDateRange = (startDate: any, endDate: any) => {
  const dates: any = []
  const currentDate = new Date(startDate)
  while (currentDate <= endDate) {
    dates.push(new Date(currentDate))
    currentDate.setDate(currentDate.getDate() + 1)
  }
  return dates.map(d => dayjs(d))
}

/**
 * Gets a human-readable relative time string for a given date.
 *
 * @param date - The date to get relative time for (Date object or date string)
 * @returns Relative time string (e.g., "2 hours ago", "in 3 days")
 *
 * @example
 * ```typescript
 * getRelativeTime(new Date(Date.now() - 3600000)) // returns "an hour ago"
 * getRelativeTime(new Date(Date.now() + 86400000)) // returns "in a day"
 * ```
 */
export const getRelativeTime = (date: any) => {
  return dayjs(date).fromNow()
}

/**
 * Calculates a human-readable label for a date range based on common presets.
 *
 * Analyzes the start and end dates to determine if they match common date range
 * patterns and returns an appropriate label. Falls back to "Custom range" if
 * no preset matches.
 *
 * @param startDate - The start date of the range
 * @param endDate - The end date of the range
 * @returns A descriptive label for the date range, or undefined if dates are invalid
 *
 * @example
 * ```typescript
 * // Assuming today is 2024-01-15
 * calculateDateRangeLabel(new Date('2024-01-01'), new Date('2024-01-15')) // returns 'This Month'
 * calculateDateRangeLabel(new Date('2023-12-16'), new Date('2024-01-15')) // returns 'Last 30 Days'
 * calculateDateRangeLabel(new Date('2023-01-01'), new Date('2023-12-31')) // returns 'Last Year'
 * calculateDateRangeLabel(new Date('2024-01-05'), new Date('2024-01-10')) // returns 'Custom range'
 * ```
 */
export const calculateDateRangeLabel = (
  startDate: Date,
  endDate: Date,
): string => {
  const today = new Date()
  const lastYearStart = startOfYear(subYears(today, 1))
  const lastYearEnd = endOfYear(subYears(today, 1))

  if (startDate && endDate) {
    if (
      differenceInDays(today, endDate) === 0 &&
      differenceInDays(today, startDate) === 30
    )
      return 'Last 30 Days'
    if (differenceInDays(startDate, subDays(endDate, 90)) === 0)
      return 'Last 90 Days'
    if (isThisMonth(startDate) && isThisMonth(endDate)) return 'This Month'
    if (
      isSameMonth(subMonths(today, 1), startDate) &&
      isSameMonth(subMonths(today, 1), endDate)
    )
      return 'Last Month'
    if (
      isThisYear(startDate) &&
      isThisYear(endDate) &&
      differenceInDays(startDate, startOfYear(today)) === 0
    )
      return 'This Year'

    if (isSameDay(endDate, lastYearEnd) && isSameDay(startDate, lastYearStart))
      return 'Last Year'

    return 'Custom range'
  }

  return 'Custom range'
}

/**
 * Convert a date string in YYYY-MM-DD format to an ISO string with start of day (00:00:00)
 *
 * @param dateString - A date string in YYYY-MM-DD format
 * @returns ISO formatted string with time set to start of day
 */
export const getStartDateISO = (dateString: string): string => {
  if (!dateString) return ''
  return dayjs(dateString).startOf('day').toISOString()
}

/**
 * Convert a date string in YYYY-MM-DD format to an ISO string with end of day (23:59:59.999)
 *
 * @param dateString - A date string in YYYY-MM-DD format
 * @returns ISO formatted string with time set to end of day
 */
export const getEndDateISO = (dateString: string): string => {
  if (!dateString) return ''
  return dayjs(dateString).endOf('day').toISOString()
}

/**
 * Validate a date string in YYYY-MM-DD format
 *
 * @param dateString - A date string in YYYY-MM-DD format
 * @returns True if the date is valid and in the correct format
 */
export const isValidDateString = (dateString: string): boolean => {
  if (!dateString) return false
  const date = dayjs(dateString, 'YYYY-MM-DD', true)
  return date.isValid() && date.format('YYYY-MM-DD') === dateString
}
